import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  BuildingStorefrontIcon,
  PlusIcon,
  TrashIcon,
  EyeIcon,
  PencilIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  StarIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentArrowDownIcon
} from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';
import { ButtonGroup } from '../components/ui/button-group';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import ProviderDetailModal from '../components/ProviderDetailModal';
import { getAppSocket } from '../lib/wesSocket';
import { confirmDialog } from '../components/ConfirmDialog';
import { Tip } from '../components/ui/tip';

interface Provider {
  id: string;
  name: string;
  companyName?: string;
  fantasyName?: string;
  type: 'individual' | 'company';
  email?: string;
  phone?: string;
  mobile?: string;
  website?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  taxId?: string;
  categories: string[] | string | null;
  specialties: string[] | string | null;
  paymentTerms: string;
  paymentMethod?: string;
  avgTermDays?: number;
  dueMode?: 'days' | 'end_of_month' | 'end_of_month_plus';
  discountBenefit?: string;
  creditLimit?: number;
  currentBalance: number;
  contactPerson?: string;
  contactPosition?: string;
  contactPhone?: string;
  contactEmail?: string;
  isActive: boolean;
  isPreferred: boolean;
  rating?: number;
  notes?: string;
  lastOrderDate?: string;
  lastContactDate?: string;
  fullAddress?: string;
  fullName?: string;
  primaryContact?: {
    name: string;
    email?: string;
    phone?: string;
  };
  isOverCreditLimit?: boolean;
  creditUtilization?: number;
  importanceLevel?: 'critico' | 'regular' | 'alternativo' | 'problematico';
  preferredScore?: number;
  preferredAuto?: boolean;
  lastPurchaseDate?: string | null;
  lastMonthTotal?: number;
  purchaseFrequency?: string;
  balanceOverdue?: number;
  balanceCurrent?: number;
  nextDueDate?: string | null;
}

const Providers: React.FC = () => {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    type: '',
    isPreferred: false,
    isActive: true,
    importance: '',
    hasBalance: false,
    overdueBalance: false,
    noPurchasesDays: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const location = useLocation();
  const navigate = useNavigate();

  // Fetch data: la primera carga muestra el spinner de página; los cambios de
  // filtros refrescan en silencio (con debounce). Si no, cada letra del buscador
  // desmontaba la página entera (spinner) y el input perdía el foco.
  const initialLoadRef = useRef(true);
  useEffect(() => {
    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      fetchProviders();
      fetchCategories();
      return;
    }
    const t = window.setTimeout(() => fetchProviders({ silent: true }), 300);
    return () => window.clearTimeout(t);
  }, [filters]);

  useEffect(() => {
    const query = new URLSearchParams(location.search).get('q');
    if (query && query !== filters.search) {
      setFilters((prev) => ({ ...prev, search: query }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Resetear página cuando cambian los filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [filters.search, filters.category, filters.type]);

  // Cálculos de paginación
  const totalPages = Math.ceil(providers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedProviders = providers.slice(startIndex, endIndex);
  const totalResults = providers.length;
  const startResult = providers.length === 0 ? 0 : startIndex + 1;
  const endResult = Math.min(endIndex, providers.length);

  // Funciones de navegación
  const goToFirstPage = () => setCurrentPage(1);
  const goToPreviousPage = () => setCurrentPage(prev => Math.max(1, prev - 1));
  const goToNextPage = () => setCurrentPage(prev => Math.min(totalPages, prev + 1));
  const goToLastPage = () => setCurrentPage(totalPages);

  const fetchProviders = async (options?: { silent?: boolean }) => {
    const silent = options?.silent === true;
    try {
      if (!silent) setLoading(true);
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.category) params.append('category', filters.category);
      if (filters.type) params.append('type', filters.type);
      if (filters.isPreferred) params.append('isPreferred', 'true');
      if (filters.importance) params.append('importance', filters.importance);
      if (filters.hasBalance) params.append('hasBalance', 'true');
      if (filters.overdueBalance) params.append('overdueBalance', 'true');
      if (filters.noPurchasesDays) params.append('noPurchasesDays', filters.noPurchasesDays);
      if (filters.isActive !== undefined) params.append('isActive', filters.isActive ? 'true' : 'false');

      const response = await axios.get(`/api/providers?${params}`);
      const sanitizeText = (text?: string) => {
        if (typeof text !== 'string') return text as any;
        return text
          .trim()
          .replace(/^\s*0+\s*/, '')
          .replace(/\s*0+\s*$/, '')
          .trim();
      };
      const normalized = Array.isArray(response.data.providers)
        ? response.data.providers.map((p: any) => ({
            ...p,
            name: sanitizeText(p.name) || p.name,
            companyName: sanitizeText(p.companyName) || p.companyName,
            isPreferred: p.isPreferred === true || p.isPreferred === 1 || p.isPreferred === '1' || p.isPreferred === 'true'
          }))
        : [];
      setProviders(normalized);
    } catch (error) {
      toast.error('Error al cargar los proveedores');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const realtimeTimerRef = useRef<number | null>(null);
  const pendingRefreshRef = useRef(false);
  useEffect(() => {
    const socket = getAppSocket();

    const scheduleSilentRefresh = (payload?: any) => {
      const path = String(payload?.path || '');
      if (
        path &&
        !(
          path.startsWith('/api/providers') ||
          path.startsWith('/api/purchases') ||
          path.startsWith('/api/products') ||
          path.startsWith('/api/supplies') ||
          path.startsWith('/api/invoices')
        )
      ) {
        return;
      }

      const modalOpen = showUploadModal || Boolean(selectedProvider);
      if (modalOpen) {
        pendingRefreshRef.current = true;
        return;
      }

      if (realtimeTimerRef.current) window.clearTimeout(realtimeTimerRef.current);
      realtimeTimerRef.current = window.setTimeout(() => {
        fetchProviders({ silent: true });
        fetchCategories();
      }, 450);
    };

    socket.on('area:update', scheduleSilentRefresh);
    socket.on('cash:update', scheduleSilentRefresh);
    socket.on('connect', scheduleSilentRefresh);

    return () => {
      if (realtimeTimerRef.current) window.clearTimeout(realtimeTimerRef.current);
      socket.off('area:update', scheduleSilentRefresh);
      socket.off('cash:update', scheduleSilentRefresh);
      socket.off('connect', scheduleSilentRefresh);
    };
  }, [showUploadModal, selectedProvider, filters.search, filters.category, filters.type]);

  useEffect(() => {
    const modalOpen = showUploadModal || Boolean(selectedProvider);
    if (modalOpen) return;
    if (!pendingRefreshRef.current) return;
    pendingRefreshRef.current = false;
    fetchProviders({ silent: true });
    fetchCategories();
  }, [showUploadModal, selectedProvider]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get('/api/providers/categories');
      setCategories(Array.isArray(response.data.categories) ? response.data.categories : []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    }
  };

  // Editar proveedor: ahora es una página (Comercial → Proveedores → editar)
  const handleEdit = (provider: Provider) => {
    setSelectedProvider(null); // Cerrar modal de detalles
    navigate(`/providers/${provider.id}/edit`);
  };

  // Delete provider
  const handleDelete = async (id: string) => {
    if (await confirmDialog('¿Estás seguro de que quieres eliminar este proveedor?')) {
      try {
        await axios.delete(`/api/providers/${id}`);
        toast.success('Proveedor eliminado correctamente');
        fetchProviders();
      } catch (error) {
        console.error('Error deleting provider:', error);
        toast.error('Error al eliminar el proveedor');
      }
    }
  };

  const handleWhatsApp = (provider: Provider) => {
    const phone = provider.phone || provider.mobile;
    if (!phone) {
      toast.error('Este proveedor no tiene número de teléfono');
      return;
    }

    let formattedPhone = phone;
    if (!phone.startsWith('+')) {
      formattedPhone = `+549${phone}`;
    }

    const message = `Hola ${provider.name}, necesito contactarte por un tema comercial.`;
    const whatsappUrl = `https://wa.me/${formattedPhone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleEmail = (provider: Provider) => {
    const email = provider.email || provider.primaryContact?.email;
    if (!email) {
      toast.error('Este proveedor no tiene dirección de email');
      return;
    }

    window.open(`mailto:${email}`, '_blank');
  };

  const getTypeLabel = (type: string) => {
    return type === 'individual' ? 'Individual' : 'Empresa';
  };

  const getCategoryLabels = (categories: string[] | string | null) => {
    const categoryMap: { [key: string]: string } = {
      neumaticos: 'Neumáticos',
      aceites: 'Aceites',
      filtros: 'Filtros',
      frenos: 'Frenos',
      motor: 'Motor',
      transmision: 'Transmisión',
      electrico: 'Eléctrico',
      carroceria: 'Carrocería',
      herramientas: 'Herramientas',
      consumibles: 'Consumibles',
      otros: 'Otros'
    };

    // Normalizar a array
    let categoryArray: string[] = [];
    if (Array.isArray(categories)) {
      categoryArray = categories;
    } else if (typeof categories === 'string' && categories.trim().length > 0) {
      categoryArray = categories.split(',').map(c => c.trim());
    }

    if (categoryArray.length === 0) return 'Sin categorías';
    return categoryArray.map(cat => categoryMap[cat] || cat).join(', ');
  };

  const formatCurrency = (value?: number | null) => {
    if (value === undefined || value === null || Number.isNaN(value)) return '$ 0,00';
    return value.toLocaleString('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 2
    });
  };

  const formatDate = (value?: string | null) => {
    if (!value) return 'Sin datos';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getImportanceBadge = (level?: Provider['importanceLevel']) => {
    switch (level) {
      case 'critico':
        return { label: 'Crítico', className: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200' };
      case 'alternativo':
        return { label: 'Alternativo', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200' };
      case 'problematico':
        return { label: 'Problemático', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200' };
      default:
        return { label: 'Regular', className: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200' };
    }
  };

  const getPaymentMethodLabel = (provider: Provider) => {
    const methodMap: Record<string, string> = {
      contado: 'Contado',
      ctacte: 'Cta Cte',
      cta_cte: 'Cta Cte',
      cheque: 'Cheque',
      transferencia: 'Transferencia',
      otro: 'Otro'
    };
    if (provider.paymentMethod) {
      return methodMap[provider.paymentMethod] || provider.paymentMethod;
    }
    const termsMap: Record<string, string> = {
      immediate: 'Contado',
      net_15: 'Crédito 15 días',
      net_30: 'Crédito 30 días',
      net_60: 'Crédito 60 días',
      net_90: 'Crédito 90 días'
    };
    return termsMap[provider.paymentTerms] || 'Sin datos';
  };

  const getTermLabel = (provider: Provider) => {
    const mode = (provider as any).dueMode || 'days';
    const d = Number(provider.avgTermDays) || 0;
    if (mode === 'end_of_month') return 'Fin de mes';
    if (mode === 'end_of_month_plus') return d > 0 ? `Fin de mes + ${d} días` : 'Fin de mes';
    if (d > 0) return `${d} días`;
    const termsMap: Record<string, string> = {
      immediate: '0 días',
      net_15: '15 días',
      net_30: '30 días',
      net_60: '60 días',
      net_90: '90 días'
    };
    return termsMap[provider.paymentTerms] || 'Sin plazo';
  };

  const getBalanceStatus = (provider: Provider): { label: string; variant: "default" | "destructive" | "outline" | "secondary" } => {
    if (!provider.isActive) return { label: 'Inactivo', variant: 'outline' };
    if (Number(provider.balanceOverdue || 0) > 0) return { label: 'Vencido', variant: 'destructive' };
    return { label: 'Al día', variant: 'secondary' };
  };

  const cleanProviderName = (provider: Provider) => {
    let originalName = '';
    // Fantasía primero: es como se conoce al negocio (muchos facturan a nombre personal)
    if ((provider as any).fantasyName) {
      originalName = (provider as any).fantasyName;
    } else if (provider.type === 'company') {
      originalName = provider.companyName || provider.name || 'Sin nombre';
    } else {
      originalName = provider.name || 'Sin nombre';
    }

    let cleanedName = originalName || '';
    if (typeof cleanedName === 'string') {
      cleanedName = cleanedName
        .trim()
        .replace(/\s*0+\s*$/, '')
        .trim();
      if (cleanedName === '') {
        cleanedName = originalName?.trim() || 'Sin nombre';
      }
    }
    return cleanedName || 'Sin nombre';
  };


  // Descargar plantilla CSV de ejemplo
  const downloadTemplate = () => {
    const csvContent = `nombre;tipo;email;telefono;cuit;direccion;ciudad;provincia;codigoPostal;activo
"Repuestos Automotor SRL";company;"repuestos@ejemplo.com";"+54 11 1234-5678";"20-12345678-9";"Av. Principal 123";"Buenos Aires";"Buenos Aires";"1234";true
"Juan Pérez";individual;"juan.perez@ejemplo.com";"+54 11 8765-4321";"20-87654321-0";"Calle Secundaria 456";"Córdoba";"Córdoba";"5000";true
"Distribuidora del Sur SA";company;"contacto@delsur.com";"+54 351 9876543";"30-98765432-1";"Ruta 9 Km 15";"Rosario";"Santa Fe";"2000";true`;

    // Agregar BOM UTF-8 para que Excel reconozca la codificación correctamente
    const BOM = '\uFEFF';
    const csvWithBOM = BOM + csvContent;

    const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'plantilla-proveedores.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Manejar subida de CSV
  const handleCsvUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Solo se permiten archivos CSV');
      return;
    }

    const formData = new FormData();
    formData.append('csvFile', file);

    try {
      setLoading(true);
      const response = await axios.post('/api/providers/upload-csv', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success(`${response.data.message || 'Proveedores importados correctamente'}`);
      setShowUploadModal(false);
      fetchProviders();

      // Resetear el input file
      event.target.value = '';
    } catch (error: any) {
      console.error('Error uploading CSV:', error);
      toast.error(error.response?.data?.error || 'Error al importar el archivo CSV');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <h2 className="text-2xl font-bold tracking-tight">Proveedores</h2>
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
          <button
            onClick={() => setShowUploadModal(true)}
            className="hidden md:inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-black dark:bg-white text-white dark:text-black px-4 text-sm font-medium shadow-sm hover:bg-black/90 dark:hover:bg-white/90 transition-colors"
          >
            <DocumentArrowDownIcon className="h-4 w-4" />
            Importar
          </button>
          <button
            onClick={() => navigate('/providers/new')}
            className="inline-flex items-center justify-center whitespace-nowrap text-sm font-bold h-8 rounded-lg gap-1.5 px-3 bg-black dark:bg-white text-white dark:text-black shadow-xs hover:bg-black/90 dark:hover:bg-white/90"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Nuevo proveedor
            <kbd className="ml-2 inline-flex items-center justify-center px-1 text-xs font-bold font-sans bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 rounded-sm border leading-none">
              N
            </kbd>
          </button>
        </div>
      </div>

            {/* Search bar */}
      <div className="space-y-3">
        <div className="relative">
          <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground pointer-events-none z-10" />
          <Input
            type="text"
            placeholder="Buscar proveedores..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="file:text-foreground placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 flex h-8 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 file:border-0 file:bg-transparent file:text-sm file:font-medium pl-8"
          />
        </div>

        <div className="grid gap-3 md:grid-cols-3">
          <Select value={filters.category || "all"} onValueChange={(value) => setFilters({ ...filters, category: value === "all" ? "" : value })}>
            <SelectTrigger className="w-full h-9">
              <SelectValue placeholder="Todas las categorías" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las categorías</SelectItem>
              {categories.map((cat: any) => (
                <SelectItem key={cat.value} value={cat.value}>
                  {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filters.type || "all"} onValueChange={(value) => setFilters({ ...filters, type: value === "all" ? "" : value })}>
            <SelectTrigger className="w-full h-9">
              <SelectValue placeholder="Todos los tipos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los tipos</SelectItem>
              <SelectItem value="individual">Individual</SelectItem>
              <SelectItem value="company">Empresa</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filters.importance || "all"} onValueChange={(value) => setFilters({ ...filters, importance: value === "all" ? "" : value })}>
            <SelectTrigger className="w-full h-9">
              <SelectValue placeholder="Criticidad" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="critico">Crítico</SelectItem>
              <SelectItem value="regular">Regular</SelectItem>
              <SelectItem value="alternativo">Alternativo</SelectItem>
              <SelectItem value="problematico">Problemático</SelectItem>
            </SelectContent>
          </Select>

          <label className="flex items-center space-x-2 md:hidden">
            <input
              type="checkbox"
              checked={filters.isPreferred}
              onChange={(e) => setFilters({ ...filters, isPreferred: e.target.checked })}
              className="rounded border-gray-300 text-primary focus:ring-primary"
            />
            <span className="text-sm">Preferidos</span>
          </label>
        </div>

        <label className="hidden md:flex items-center space-x-2">
          <input
            type="checkbox"
            checked={filters.isPreferred}
            onChange={(e) => setFilters({ ...filters, isPreferred: e.target.checked })}
            className="rounded border-gray-300 text-primary focus:ring-primary"
          />
          <span className="text-sm">Preferidos</span>
        </label>
        <div className="flex flex-wrap gap-3">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={filters.hasBalance}
              onChange={(e) => setFilters({ ...filters, hasBalance: e.target.checked })}
              className="rounded border-gray-300 text-primary focus:ring-primary"
            />
            <span className="text-sm">Con saldo pendiente</span>
          </label>
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={filters.overdueBalance}
              onChange={(e) => setFilters({ ...filters, overdueBalance: e.target.checked })}
              className="rounded border-gray-300 text-primary focus:ring-primary"
            />
            <span className="text-sm">Con saldo vencido</span>
          </label>
          <div className="flex items-center gap-2">
            <span className="text-sm">Sin compras (días)</span>
            <Input
              type="number"
              value={filters.noPurchasesDays}
              onChange={(e) => setFilters({ ...filters, noPurchasesDays: e.target.value })}
              className="h-8 w-24"
              min={0}
            />
          </div>
        </div>
      </div>
{/* Providers List */}
      <Card className="border-0 shadow-none md:border md:shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600"></div>
            </div>
          ) : providers.length === 0 ? (
            <div className="text-center py-12">
              <BuildingStorefrontIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No hay proveedores</p>
            </div>
          ) : (
            <>
              <div className="hidden md:block relative w-full overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-muted/50 border-b">
  <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Proveedor</TableHead>
  <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Criticidad</TableHead>
  <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Condición</TableHead>
  <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Última compra</TableHead>
  <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Balance</TableHead>
  <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Estado</TableHead>
  <TableHead className="text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap"></TableHead>
</TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedProviders.map((provider) => (
                      <TableRow key={provider.id} className="border-b hover:bg-muted/50 cursor-pointer">
                        <TableCell className="p-2 align-middle whitespace-nowrap text-left">
                          <div className="flex items-center gap-2">
                            {(provider as any).fantasyName ? (
                              <Tip label={`Razón social: ${provider.name}`}>
                                <span className="font-medium cursor-default">{cleanProviderName(provider)}</span>
                              </Tip>
                            ) : (
                              <span className="font-medium">{cleanProviderName(provider)}</span>
                            )}
                            {provider.isPreferred && <StarIcon className="h-4 w-4 text-yellow-500" />}
                            {provider.preferredAuto && (
                              <Badge variant="secondary" className="text-[10px] px-2 py-0.5">
                                Auto
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="p-2 align-middle whitespace-nowrap text-left">
                          {(() => {
                            const badge = getImportanceBadge(provider.importanceLevel);
                            return (
                              <Badge className={badge.className}>
                                {badge.label}
                              </Badge>
                            );
                          })()}
                        </TableCell>
                        <TableCell className="p-2 align-middle whitespace-nowrap text-left">
                          <div className="text-sm">{getPaymentMethodLabel(provider)}</div>
                        </TableCell>
                        <TableCell className="p-2 align-middle whitespace-nowrap text-left">
                          <div className="text-sm">
                            {provider.lastPurchaseDate ? formatDate(provider.lastPurchaseDate) : 'Sin compras'}
                          </div>
                        </TableCell>
                        <TableCell className="p-2 align-middle whitespace-nowrap text-left">
                          <div className="text-sm space-y-1">
                            {Number(provider.balanceOverdue || 0) > 0 && (
                              <div className="text-destructive font-medium">
                                Vencido: {formatCurrency(provider.balanceOverdue || 0)}
                              </div>
                            )}
                            {Number(provider.balanceCurrent || 0) > 0 && (
                              <div>
                                Al dia: {formatCurrency(provider.balanceCurrent || 0)}
                              </div>
                            )}
                            {Number(provider.balanceOverdue || 0) + Number(provider.balanceCurrent || 0) === 0 && (
                              <div className="text-muted-foreground">Sin saldo</div>
                            )}
                            {provider.nextDueDate && (
                              <div className="text-xs text-muted-foreground">Vence: {formatDate(provider.nextDueDate)}</div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="p-2 align-middle whitespace-nowrap text-left">
                          {(() => {
                            const status = getBalanceStatus(provider);
                            return <Badge variant={status.variant}>{status.label}</Badge>;
                          })()}
                        </TableCell>
                        <TableCell className="p-2 align-middle whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end">
                            <ButtonGroup>
                              <Tip label="Ver detalles">
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedProvider(provider);
                                }}
                                className="size-8"
                              >
                                <EyeIcon className="h-4 w-4" />
                              </Button>
                              </Tip>
                              <Tip label="WhatsApp">
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleWhatsApp(provider);
                                }}
                                className="size-8"
                              >
                                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                                </svg>
                              </Button>
                              </Tip>
                              <Tip label="Email">
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEmail(provider);
                                }}
                                className="size-8"
                              >
                                <EnvelopeIcon className="h-4 w-4" />
                              </Button>
                              </Tip>
                              <Tip label="Eliminar">
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(provider.id);
                                }}
                                className="size-8"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </Button>
                              </Tip>
                            </ButtonGroup>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="md:hidden space-y-3 p-4">
                {paginatedProviders.map((provider) => (
                  <div key={provider.id} className="rounded-xl border bg-card/80 p-4 shadow-sm space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-xs text-muted-foreground">Proveedor</p>
                        <p className="text-base font-semibold">{cleanProviderName(provider)}</p>
                        {(provider as any).fantasyName && (
                          <p className="text-xs text-muted-foreground">{provider.name}</p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        {(() => {
                          const badge = getImportanceBadge(provider.importanceLevel);
                          return (
                            <Badge className={badge.className}>
                              {badge.label}
                            </Badge>
                          );
                        })()}
                        {provider.isPreferred && (
                          <span className="text-xs text-amber-600 flex items-center gap-1">
                            <StarIcon className="h-3 w-3" />
                            Preferido
                          </span>
                        )}
                        {provider.preferredAuto && (
                          <Badge variant="secondary" className="text-[10px] px-2 py-0.5">
                            Auto
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-xs uppercase text-muted-foreground">Condiciones</p>
                        <p>{getPaymentMethodLabel(provider)}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-muted-foreground">Historial</p>
                        <p>{provider.lastPurchaseDate ? formatDate(provider.lastPurchaseDate) : 'Sin compras'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-muted-foreground">Balance</p>
                        {Number(provider.balanceOverdue || 0) > 0 && (
                          <p className="text-destructive font-semibold">Vencido: {formatCurrency(provider.balanceOverdue || 0)}</p>
                        )}
                        {Number(provider.balanceCurrent || 0) > 0 && (
                          <p>Al dia: {formatCurrency(provider.balanceCurrent || 0)}</p>
                        )}
                        {Number(provider.balanceOverdue || 0) + Number(provider.balanceCurrent || 0) === 0 && (
                          <p className="text-muted-foreground">Sin saldo</p>
                        )}
                        {provider.nextDueDate && (
                          <p className="text-xs text-muted-foreground">Vence: {formatDate(provider.nextDueDate)}</p>
                        )}
                      </div>
                      <div>
                        <p className="text-xs uppercase text-muted-foreground">Estado</p>
                        {(() => {
                          const status = getBalanceStatus(provider);
                          return (
                            <Badge variant={status.variant} className="mt-1 w-fit">
                              {status.label}
                            </Badge>
                          );
                        })()}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 min-w-[110px] justify-center"
                        onClick={() => setSelectedProvider(provider)}
                      >
                        <EyeIcon className="h-4 w-4 mr-1" />
                        Ver
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 min-w-[110px] justify-center"
                        onClick={() => handleWhatsApp(provider)}
                      >
                        <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                        </svg>
                        WhatsApp
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 min-w-[110px] justify-center"
                        onClick={() => handleEmail(provider)}
                      >
                        <EnvelopeIcon className="h-4 w-4 mr-1" />
                        Email
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 min-w-[110px] justify-center"
                        onClick={() => handleEdit(provider)}
                      >
                        <PencilIcon className="h-4 w-4 mr-1" />
                        Editar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 min-w-[110px] justify-center text-destructive"
                        onClick={() => handleDelete(provider.id)}
                      >
                        <TrashIcon className="h-4 w-4 mr-1" />
                        Eliminar
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Paginación */}
      {!loading && providers.length > 0 && (
        <div className="flex items-center justify-between px-2">
          <div className="hidden flex-1 text-muted-foreground text-sm md:block">
            Mostrando {startResult} de {totalResults} resultados
          </div>
          <div className="flex items-center space-x-6 lg:space-x-8">
            <div className="flex w-[100px] items-center justify-center font-medium text-sm">
              Página {currentPage} de {totalPages}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="icon"
              onClick={goToFirstPage}
              disabled={currentPage === 1}
              className="shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium text-sm outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0 border bg-card shadow-xs hover:bg-accent hover:text-accent-foreground active:scale-[0.97] dark:border-input dark:bg-input/30 dark:hover:bg-input/50 size-9 hidden h-8 w-8 lg:flex"
            >
              <span className="sr-only">Ir a la primera página</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                <path d="m11 17-5-5 5-5"></path>
                <path d="m18 17-5-5 5-5"></path>
              </svg>
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              className="inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium text-sm outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0 border bg-card shadow-xs hover:bg-accent hover:text-accent-foreground active:scale-[0.97] dark:border-input dark:bg-input/30 dark:hover:bg-input/50 size-9 h-8 w-8"
            >
              <span className="sr-only">Ir a la página anterior</span>
              <ChevronLeftIcon className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              className="inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium text-sm outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0 border bg-card shadow-xs hover:bg-accent hover:text-accent-foreground active:scale-[0.97] dark:border-input dark:bg-input/30 dark:hover:bg-input/50 size-9 h-8 w-8"
            >
              <span className="sr-only">Ir a la página siguiente</span>
              <ChevronRightIcon className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={goToLastPage}
              disabled={currentPage === totalPages}
              className="shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium text-sm outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0 border bg-card shadow-xs hover:bg-accent hover:text-accent-foreground active:scale-[0.97] dark:border-input dark:bg-input/30 dark:hover:bg-input/50 size-9 hidden h-8 w-8 lg:flex"
            >
              <span className="sr-only">Ir a la última página</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                <path d="m6 17 5-5-5-5"></path>
                <path d="m13 17 5-5-5-5"></path>
              </svg>
            </Button>
          </div>
        </div>
      )}

      {/* Provider Detail Modal */}
      {selectedProvider && (
        <ProviderDetailModal
          provider={selectedProvider!}
          isOpen={!!selectedProvider}
          onClose={() => setSelectedProvider(null)}
          onEdit={handleEdit}
        />
      )}

      {/* Modal de importación CSV */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Importar desde CSV</DialogTitle>
            <DialogDescription>
              Importa proveedores desde un archivo CSV
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="csv-file">Archivo CSV</Label>
              <Input
                id="csv-file"
                type="file"
                accept=".csv"
                onChange={handleCsvUpload}
              />
            </div>

            <div className="flex justify-between items-center">
              <Button
                type="button"
                onClick={() => downloadTemplate()}
                variant="outline"
                size="sm"
              >
                <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                Descargar Plantilla
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowUploadModal(false)}
              >
                Cancelar
              </Button>
            </div>

            <details className="rounded-lg border bg-muted/50">
              <summary className="p-3 cursor-pointer font-medium hover:bg-muted/80">
                Ver formato del CSV y campos
              </summary>
              <div className="p-3 pt-0 border-t space-y-3">
                <div>
                  <h5 className="font-medium mb-2 text-sm">Columnas requeridas:</h5>
                  <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                    <div><strong>nombre</strong> - Nombre (requerido)</div>
                    <div><strong>tipo</strong> - individual o company (requerido)</div>
                    <div><strong>email</strong> - Email (opcional)</div>
                    <div><strong>telefono</strong> - Teléfono (opcional)</div>
                    <div><strong>cuit</strong> - CUIT/CUIL (opcional)</div>
                    <div><strong>direccion</strong> - Dirección (opcional)</div>
                    <div><strong>ciudad</strong> - Ciudad (opcional)</div>
                    <div><strong>provincia</strong> - Provincia (opcional)</div>
                    <div><strong>codigoPostal</strong> - Código Postal (opcional)</div>
                    <div><strong>activo</strong> - true/false (opcional)</div>
                  </div>
                </div>

                <div>
                  <h5 className="font-medium mb-2 text-sm">Tipos válidos:</h5>
                  <p className="text-xs text-muted-foreground">
                    <strong>individual</strong> - Para personas físicas<br />
                    <strong>company</strong> - Para empresas
                  </p>
                </div>

                <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-800">
                  <p className="text-xs text-blue-800 dark:text-blue-200">
                    <strong>Consejo:</strong> Usa "Descargar Plantilla" para obtener un archivo CSV de ejemplo con el formato correcto
                  </p>
                </div>
              </div>
            </details>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Providers;


